import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ProjectCard from '../components/ProjectCard';
import { useProjects, type ProjectPhaseFilter, type ProjectSortOption } from '../hooks';
import { PROJECT_PHASES } from '../types';

const phaseFilters: { value: ProjectPhaseFilter; label: string; emoji: string; icon: string }[] = [
  { value: 'all', label: 'All Projects', emoji: '', icon: 'apps' },
  { value: 'IDEATION', label: PROJECT_PHASES.IDEATION.label, emoji: PROJECT_PHASES.IDEATION.emoji, icon: PROJECT_PHASES.IDEATION.icon },
  { value: 'DEVELOPMENT', label: PROJECT_PHASES.DEVELOPMENT.label, emoji: PROJECT_PHASES.DEVELOPMENT.emoji, icon: PROJECT_PHASES.DEVELOPMENT.icon },
  { value: 'PRESENTATION', label: PROJECT_PHASES.PRESENTATION.label, emoji: PROJECT_PHASES.PRESENTATION.emoji, icon: PROJECT_PHASES.PRESENTATION.icon },
  { value: 'DELIVERED', label: PROJECT_PHASES.DELIVERED.label, emoji: PROJECT_PHASES.DELIVERED.emoji, icon: PROJECT_PHASES.DELIVERED.icon },
  { value: 'ARCHIVED', label: PROJECT_PHASES.ARCHIVED.label, emoji: PROJECT_PHASES.ARCHIVED.emoji, icon: PROJECT_PHASES.ARCHIVED.icon },
];

const sortOptions: { value: ProjectSortOption; label: string }[] = [
  { value: 'votes', label: 'Most Voted' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'alphabetical', label: 'A-Z' },
];

const ExplorePage: React.FC = () => {
  const {
    projects,
    filter,
    setFilter,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    totalCount,
    filteredCount,
    isLoading,
    error,
    refetch,
  } = useProjects();

  return (
    <>
      {/* Hero Section */}
      <section className="w-full border-b border-border bg-gradient-to-b from-primary/5 to-transparent py-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-3">
                Explore Projects
              </h1>
              <p className="text-muted-foreground text-lg max-w-xl">
                Discover and contribute to agent-powered projects shaping the future of decentralized technology.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-sm px-3 py-1">
                <span className="material-symbols-outlined text-base mr-1">grid_view</span>
                {totalCount} Total Projects
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Content */}
      <section className="w-full py-8 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                search
              </span>
              <input
                type="text"
                placeholder="Search projects by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-secondary/50 border border-border rounded-lg pl-10 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm hidden sm:block">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as ProjectSortOption)}
                className="bg-secondary/50 border border-border rounded-lg pl-4 pr-8 py-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Phase Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {phaseFilters.map((phaseFilter) => (
              <Button
                key={phaseFilter.value}
                variant={filter === phaseFilter.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(phaseFilter.value)}
                className={filter === phaseFilter.value ? 'shadow-[0_0_15px_rgba(244,37,37,0.2)]' : ''}
              >
                {phaseFilter.emoji ? (
                  <span className="mr-1">{phaseFilter.emoji}</span>
                ) : (
                  <span className="material-symbols-outlined text-base mr-1">{phaseFilter.icon}</span>
                )}
                {phaseFilter.label}
              </Button>
            ))}
          </div>

          {/* Results Count */}
          {filter !== 'all' || searchQuery ? (
            <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
              <span className="material-symbols-outlined text-base">filter_list</span>
              Showing {filteredCount} of {totalCount} projects
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="ml-2 text-primary hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : null}

          {/* Project Grid */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-6xl text-muted-foreground mb-4 animate-spin">
                progress_activity
              </span>
              <h3 className="text-xl font-semibold text-foreground mb-2">Loading projects...</h3>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-6xl text-destructive mb-4">
                error
              </span>
              <h3 className="text-xl font-semibold text-foreground mb-2">Failed to load projects</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={refetch}>
                Try again
              </Button>
            </div>
          ) : projects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <Link key={project.id} to={`/project/${project.id}`}>
                  <ProjectCard project={project} />
                </Link>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="material-symbols-outlined text-6xl text-muted-foreground mb-4">
                search_off
              </span>
              <h3 className="text-xl font-semibold text-foreground mb-2">No projects found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your filters or search query
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setFilter('all');
                  setSearchQuery('');
                }}
              >
                Clear all filters
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ExplorePage;
